import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { ThemeService } from '@shared/services/theme.service';

/** `nav`: la bolsa se inclina al pasar el cursor · `login`: entra con rebote y luego el texto · `loading`: la bolsa flota. */
export type ProsellLogoMode = 'nav' | 'login' | 'loading';

/**
 * Logo de ProSell. Dibuja dos capas de la misma imagen recortadas con `clip-path` —
 * la bolsa y el texto— para animarlas por separado sin alterar el dibujo. El ancho lo pone quien lo usa (clase en el
 * host); el alto sale de la proporción.
 */
@Component({
  selector: 'app-prosell-logo',
  standalone: true,
  template: `
    @if (imageFailed()) {
      <span class="fallback text-base font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-lg">ProSell</span>
    } @else {
      <span class="capa">
        <img class="bolsa" [src]="src()" alt="" draggable="false" (error)="imageFailed.set(true)" />
        <img class="texto" [src]="src()" alt="" draggable="false" (error)="imageFailed.set(true)" />
      </span>
    }
  `,
  styles: `
    :host { display: block; position: relative; aspect-ratio: 668 / 183; isolation: isolate; }
    .capa, img { position: absolute; inset: 0; }
    img { display: block; width: 100%; height: 100%; user-select: none; pointer-events: none; }
    /* La bolsa ocupa el 26,5 % izquierdo de la imagen; el texto, el resto. */
    .bolsa { clip-path: inset(0 73.5% 0 0); transform-origin: 12.6% 50%; }
    .texto { clip-path: inset(0 0 0 26.5%); }
    .fallback { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }

    :host(.mode-login) .bolsa { animation: bolsa-entra .75s cubic-bezier(.16, 1.15, .31, 1) both; }
    :host(.mode-login) .texto { animation: texto-entra .65s ease .25s both; }
    :host(.mode-loading) .bolsa { animation: bolsa-flota 1.65s ease-in-out infinite; }
    :host(.mode-nav) .bolsa { transition: transform .32s ease; }
    @media (hover: hover) and (pointer: fine) {
      :host(.mode-nav:hover) .bolsa, :host-context(a:hover) .bolsa { transform: translateY(-5%) rotate(-9deg); }
    }
    :host-context(a:focus-visible) .bolsa { transform: translateY(-5%) rotate(-9deg); }

    @keyframes bolsa-entra {
      0% { opacity: 0; transform: translateY(12%) scale(.89); }
      65% { opacity: 1; transform: translateY(-4%) scale(1.03); }
      100% { opacity: 1; transform: none; }
    }
    @keyframes texto-entra { from { opacity: 0; transform: translateX(-4%); } to { opacity: 1; transform: none; } }
    @keyframes bolsa-flota { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7%); } }

    @media (prefers-reduced-motion: reduce) {
      .bolsa, .texto { animation: none !important; transition: none !important; }
    }
  `,
  host: {
    role: 'img',
    'aria-label': 'ProSell',
    '[class.mode-nav]': "mode() === 'nav'",
    '[class.mode-login]': "mode() === 'login'",
    '[class.mode-loading]': "mode() === 'loading'"
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProsellLogoComponent {
  private readonly themeService = inject(ThemeService);
  readonly mode = input<ProsellLogoMode>('nav');
  readonly tone = input<'auto' | 'dark'>('auto');
  readonly src = computed(() => this.tone() === 'dark' || this.themeService.theme() === 'dark'
    ? 'assets/images/prosell/prosell-logo-dark.webp'
    : 'assets/images/prosell/prosell-logo.webp');
  readonly imageFailed = signal(false);

  constructor() {
    effect(() => {
      this.src();
      this.imageFailed.set(false);
    });
  }
}
